/**
 * Pure click-vs-drag classifier for a single pointer press.
 * A press that travels less than `threshold` screen px before release is a
 * click; once it exceeds the threshold it becomes a drag (camera pan) and
 * will not also produce a click.
 */
export class PointerGesture {
  private startX = 0;
  private startY = 0;
  private lastX = 0;
  private lastY = 0;
  private active = false;
  private dragging = false;

  private threshold: number;

  constructor(private readonly defaultThreshold: number) {
    this.threshold = defaultThreshold;
  }

  get isActive(): boolean {
    return this.active;
  }

  get isDragging(): boolean {
    return this.dragging;
  }

  /** Start a press. `threshold` overrides the default for this press (e.g. larger for touch). */
  down(x: number, y: number, threshold = this.defaultThreshold): void {
    this.threshold = threshold;
    this.active = true;
    this.dragging = false;
    this.startX = this.lastX = x;
    this.startY = this.lastY = y;
  }

  /** Returns the pan delta to apply for this move (0,0 while still a potential click). */
  move(x: number, y: number): { dx: number; dy: number } {
    if (!this.active) return { dx: 0, dy: 0 };
    if (!this.dragging && Math.hypot(x - this.startX, y - this.startY) >= this.threshold) {
      this.dragging = true;
      // Apply the movement accumulated before the threshold so the map does not "jump".
      this.lastX = this.startX;
      this.lastY = this.startY;
    }
    if (!this.dragging) return { dx: 0, dy: 0 };
    const d = { dx: x - this.lastX, dy: y - this.lastY };
    this.lastX = x;
    this.lastY = y;
    return d;
  }

  /** Ends the press. Returns true if it was a click (not a drag). */
  up(): boolean {
    const wasClick = this.active && !this.dragging;
    this.active = false;
    this.dragging = false;
    return wasClick;
  }

  cancel(): void {
    this.active = false;
    this.dragging = false;
  }
}
